import React from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import Spinner from "../spinner/Spinner";
import { useSupplyVendorById } from "../../hooks/useInventory";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";

const SupplyVendorShow = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const vendorId = searchParams.get("id");
  const { data, isLoading } = useSupplyVendorById(vendorId);
  const vendor = data?.data;

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/head-office/inventory/supply-vendors")} className="text-orange1 hover:text-orange-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Vendor Details</h1>
            {vendor && <p className="text-gray-500 text-sm">{vendor.name}</p>}
          </div>
        </div>

        {isLoading ? (
          <Spinner loading />
        ) : !vendor ? (
          <div className="bg-white rounded-lg shadow p-6 text-red-500 text-center">Vendor not found.</div>
        ) : (
          <>
            {/* Financial summary */}
            {vendor.financial_summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[
                  { label: "Total Purchased", value: vendor.financial_summary.total_purchased, color: "text-gray-800" },
                  { label: "Total Paid", value: vendor.financial_summary.total_paid, color: "text-green-600" },
                  { label: "Outstanding Balance", value: vendor.financial_summary.outstanding_balance, color: "text-red-600" },
                ].map((card) => (
                  <div key={card.label} className="bg-white rounded-lg shadow p-5">
                    <p className="text-gray-500 text-sm">{card.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${card.color}`}>
                      <CurrencyFormatter amount={card.value || 0} />
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-lg font-semibold">Vendor Information</h2>
                <Link
                  to={`/head-office/inventory/supply-vendors/edit?id=${vendor.id}`}
                  className="text-orange1 hover:text-orange-600 text-sm font-medium"
                >
                  Edit Vendor
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {[
                  ["Name", vendor.name],
                  ["Phone", vendor.phone_number],
                  ["Email", vendor.email || "-"],
                  ["Status", vendor.status],
                  ["Contact Person", vendor.contact_person_name || "-"],
                  ["Contact Phone", vendor.contact_person_phone || "-"],
                  ["Address", vendor.address || "-"],
                  ["Products Supplied", vendor.products_supplied || "-"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{value}</p>
                  </div>
                ))}
                {vendor.narration && (
                  <div className="md:col-span-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Notes</p>
                    <p className="text-sm text-gray-700 mt-0.5">{vendor.narration}</p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/head-office/inventory/reconciliation?vendor_id=${vendor.id}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
                >
                  View Reconciliation
                </Link>
                <Link
                  to={`/head-office/inventory/vendor-payments?vendor_id=${vendor.id}`}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition text-sm"
                >
                  View Payments
                </Link>
                <Link
                  to={`/head-office/inventory/admin-purchases?vendor_id=${vendor.id}`}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition text-sm"
                >
                  View Purchases
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayouts>
  );
};

export default SupplyVendorShow;
